import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
  TextField,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { 
  LocalShipping, 
  ArrowForward,
  Refresh,
  Settings,
  Save
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { getAuthToken } from '../../../utils/cookieUtils';
// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Color configuration (copied from DockManagement.tsx)
const colorConfig = {
  mdt: {
    warning: '#f97316',
    critical: '#ef4444',
    good: '#22c55e',
    warningThreshold: 30,
    criticalThreshold: 60,
    textColor: '#ffffff'
  },
  detention: {
    warning: '#f97316',
    critical: '#ef4444',
    good: '#22c55e',
    warningThreshold: 30,
    criticalThreshold: 60,
    textColor: '#ffffff'
  },
  card: {
    default: '#ffffff',
    oneWarning: '#fef3c7',
    twoWarnings: '#fde68a',
    threeWarnings: '#fcd34d',
    oneCritical: '#fee2e2',
    twoCriticals: '#fecaca',
    threeCriticals: '#fca5a5',
    mixed: '#f3f4f6',
    textColor: '#1f2937'
  }
};

// Helper function to extract minutes from timer string (copied from DockManagement.tsx)
const getMinutesFromTimer = (timeString: string): number => {
  if (!timeString) return 0;
  
  // Handle negative times (starting with -)
  const isNegative = timeString.startsWith('-');
  const cleanTimeString = isNegative ? timeString.substring(1) : timeString;
  
  const parts = cleanTimeString.split(':');
  if (parts.length >= 2) {
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    // If seconds are included, handle them too
    const seconds = parts.length > 2 ? (parseInt(parts[2], 10) || 0) / 60 : 0;
    
    return (hours * 60 + minutes + seconds) * (isNegative ? -1 : 1);
  }
  
  return 0;
};

// Helper functions for timer calculations (copied from DockManagement)
const calculateCountdownTime = (targetDateString: string, timezone = 'America/New_York'): string => {
  try {
    const targetDate = new Date(targetDateString);
    const now = new Date();
    const diffMs = targetDate.getTime() - now.getTime();
    
    if (diffMs <= 0) {
      // Past the deadline
      const absDiffMs = Math.abs(diffMs);
      const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
      const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `-${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    } else {
      // Time remaining
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    }
  } catch (error) {
    return "00:00:00";
  }
};

// Helper function to get time status (copied from DockManagement.tsx)
const getTimeStatus = (timeString: string, timerType: 'mdt' | 'detention'): 'red' | 'green' | 'orange' => {
  // For MDT and detention, if there's no data or it's 00:00:00, treat as green (N/A state)
  if (!timeString || timeString === '00:00:00') {
    return 'green';
  }
  
  const minutes = getMinutesFromTimer(timeString);
  const config = colorConfig[timerType];
  
  // For MDT and detention: check thresholds for both remaining time and overdue time
  if (minutes > 0) {
    // Time remaining - check if we're approaching deadlines
    if (minutes <= config.criticalThreshold) {
      return 'red'; // Critical - very little time remaining
    } else if (minutes <= config.warningThreshold) {
      return 'orange'; // Warning - getting close to deadline
    }
    return 'green'; // Good - plenty of time remaining
  } else {
    // Overdue - always critical
    return 'red';
  }
};

// Types
interface Facility {
  id: number;
  name: string;
}

interface Driver {
  id: string;
  shipmentId: string;
  driver?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  shipment?: {
    id: string;
    referenceNumber: string;
    direction?: string;
    metadata?: any;
    trailer?: {
      trailer_number?: string;
      carrier?: {
        name: string;
      };
    };
  };
  status: string;
  checkIn: string | null;
  createdAt: string;
  updatedAt: string;
  mdt?: string;
  detention?: string;
}

// Widget configuration interface
interface DriverWidgetConfig {
  showOnlyNeedingAttention: boolean;
  showRecentOnly: boolean;
  recentHoursThreshold: number;
  showStatusCount: boolean;
  sortBy: 'name' | 'status' | 'checkIn' | 'shipment' | 'createdAt';
  sortOrder: 'asc' | 'desc';
  statusFilter: 'all' | 'checked_in' | 'pending' | 'assigned';
}

interface DriverDashboardWidgetProps {
  selectedFacility: Facility | null;
  compactMode: boolean;
  data?: Record<string, any> | null;
  isRefreshing?: boolean;
  onRefresh?: () => void;
}

// Helper function to get display name for driver
const getDriverDisplayName = (driver: Driver): string => {
  return driver.driver 
    ? `${driver.driver.firstName} ${driver.driver.lastName}`.trim()
    : `Driver ${driver.id}`;
};

// Helper function to get time elapsed since creation
const getTimeElapsed = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  } catch (error) {
    return 'Unknown';
  }
};

// Helper function to determine if driver needs attention
const needsAttention = (driver: Driver, hoursThreshold: number): boolean => {
  // Drivers with pending status or those without check-in for extended time
  if (driver.status === 'pending' || driver.status === 'arrived' || driver.status === 'waiting') return true;
  
  // Check MDT and detention urgency
  const mdtStatus = getTimeStatus(driver.mdt || '00:00:00', 'mdt');
  const detentionStatus = getTimeStatus(driver.detention || '00:00:00', 'detention');
  
  // If any timer is critical or orange, needs attention
  if (mdtStatus === 'red' || detentionStatus === 'red' || mdtStatus === 'orange' || detentionStatus === 'orange') {
    return true;
  }
  
  if (driver.createdAt) {
    const createdTime = new Date(driver.createdAt);
    const hoursElapsed = (Date.now() - createdTime.getTime()) / (1000 * 60 * 60);
    return hoursElapsed >= hoursThreshold;
  }
  
  return false;
};

// Helper function to get status color
const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'checked_in':
    case 'active':
      return '#22c55e'; // green
    case 'pending':
    case 'arrived':
    case 'waiting':
      return '#f97316'; // orange
    case 'assigned':
      return '#3b82f6'; // blue
    case 'in_progress':
      return '#8b5cf6'; // purple
    default:
      return '#6b7280'; // gray
  }
};

// Helper function to get driver card background color based on MDT/detention urgency
const getDriverCardColor = (driver: Driver, hoursThreshold: number): string => {
  // Get timer statuses
  const mdtStatus = getTimeStatus(driver.mdt || '00:00:00', 'mdt');
  const detentionStatus = getTimeStatus(driver.detention || '00:00:00', 'detention');
  
  const statuses = [mdtStatus, detentionStatus];
  const warningCount = statuses.filter(status => status === 'orange').length;
  const criticalCount = statuses.filter(status => status === 'red').length;

  // Apply timer-based coloring first (highest priority)
  if (criticalCount >= 2) return colorConfig.card.twoCriticals;
  if (criticalCount === 1) return colorConfig.card.oneCritical;
  if (warningCount >= 2) return colorConfig.card.twoWarnings;
  if (warningCount === 1) return colorConfig.card.oneWarning;
  if (warningCount > 0 || criticalCount > 0) return colorConfig.card.mixed;
  
  // Fall back to status-based coloring for non-timer issues
  const elapsed = driver.createdAt ? (Date.now() - new Date(driver.createdAt).getTime()) / (1000 * 60 * 60) : 0;
  
  if (driver.status === 'pending' || driver.status === 'arrived' || driver.status === 'waiting') {
    if (elapsed > hoursThreshold * 2) return '#fee2e2'; // red background
    if (elapsed > hoursThreshold) return '#fef3c7'; // yellow background
    return '#fef2f2'; // light red background
  }
  
  return colorConfig.card.default; // white background
};

// Helper function to get display status for driver
const getDisplayStatus = (driver: Driver): string => {
  switch (driver.status?.toLowerCase()) {
    case 'checked_in':
      return 'CHECKED IN';
    case 'pending':
      return 'PENDING';
    case 'assigned':
      return 'ASSIGNED';
    case 'arrived':
      return 'ARRIVED';
    case 'waiting':
      return 'WAITING';
    case 'active':
      return 'ACTIVE';
    case 'in_progress':
      return 'IN PROGRESS';
    default:
      return ""
  }
};

const DriverDashboardWidget: React.FC<DriverDashboardWidgetProps> = ({
  selectedFacility,
  compactMode,
  data: dashboardData,
  isRefreshing = false,
  onRefresh
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [saving, setSaving] = useState(false);

  // Widget configuration with localStorage persistence
  const [config, setConfig] = useState<DriverWidgetConfig>(() => {
    const saved = localStorage.getItem('driverWidgetConfig');
    return saved ? JSON.parse(saved) : {
      showOnlyNeedingAttention: true,
      showRecentOnly: true,
      recentHoursThreshold: 24,
      showStatusCount: true,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      statusFilter: 'all'
    };
  });

  // Save config to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('driverWidgetConfig', JSON.stringify(config));
  }, [config]);

  // Process raw data from consolidated query
  const drivers: Driver[] = React.useMemo(() => {
    const raw = dashboardData?.driverAssignments;
    if (!raw?.success) return [];
    const rawDrivers = raw.driverAssignments || [];
    return rawDrivers.map((driver: any) => {
      let mdt = "";
      let detention = "";
      if (driver.shipment?.metadata) {
        try {
          const metadata = typeof driver.shipment.metadata === 'string'
            ? JSON.parse(driver.shipment.metadata)
            : driver.shipment.metadata;
          if (metadata.appointment_must_depart_by_date) {
            mdt = calculateCountdownTime(metadata.appointment_must_depart_by_date);
          }
          if (metadata.detention_time) {
            detention = calculateCountdownTime(metadata.detention_time);
          }
        } catch (err) {
          console.error('Error parsing shipment metadata:', err);
        }
      }
      return { ...driver, mdt, detention };
    });
  }, [dashboardData?.driverAssignments]);

  const loading = !dashboardData && !error;

  // Handle driver click - navigate to driver management with driver context
  const handleDriverClick = (driver: Driver) => {
    const actualDriverId = driver.driver?.id || driver.id;
    console.log('Dashboard widget: Navigating with driver ID:', actualDriverId);
    console.log('Dashboard widget: Full driver object:', driver);
    navigate(`/drivers/unassigned_slot?driver=${actualDriverId}`);
  };

  // Navigate to full driver management page
  const handleViewAll = () => {
    navigate('/drivers/unassigned_slot');
  };

  // Save widget configuration to facility
  const handleSaveConfig = async () => {
    if (!selectedFacility) return;

    try {
      setSaving(true);

      // Get current facility configuration
      const facilityQuery = `
        query GetFacility {
          facility(id: "${selectedFacility.id}") {
            id
            name
            configuration
          }
        }
      `;

      const authToken = getAuthToken();

      const facilityResponse = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ query: facilityQuery })
      });

      const facilityResult = await facilityResponse.json();
      if (facilityResult.errors) {
        throw new Error(facilityResult.errors[0].message);
      }

      // Parse existing configuration
      const existingConfig = facilityResult.data.facility?.configuration ? 
        (typeof facilityResult.data.facility.configuration === 'string' ? 
          JSON.parse(facilityResult.data.facility.configuration) : 
          facilityResult.data.facility.configuration) : {};

      // Update widgets configuration
      const updatedConfig = {
        ...existingConfig,
        widgets: {
          ...existingConfig.widgets,
          driverDashboard: config
        }
      };

      // Save updated configuration
      const updateMutation = `
        mutation UpdateFacility($id: ID!, $input: FacilityUpdateInput!) {
          update_facility(id: $id, input: $input) {
            id
            name
            configuration
          }
        }
      `;

      const updateResponse = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: updateMutation,
          variables: {
            id: selectedFacility.id,
            input: {
              name: facilityResult.data.facility.name,
              configuration: JSON.stringify(updatedConfig)
            }
          }
        })
      });

      const updateResult = await updateResponse.json();
      if (updateResult.errors) {
        throw new Error(updateResult.errors[0].message);
      }

      console.log('Widget configuration saved successfully');
    } catch (err) {
      console.error('Failed to save widget configuration:', err);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Filter and sort drivers based on configuration
  let filteredDrivers = [...drivers];
  
  // Apply status filter
  if (config.statusFilter !== 'all') {
    filteredDrivers = filteredDrivers.filter(driver => 
      driver.status.toLowerCase() === config.statusFilter
    );
  }
  
  // Apply recent filter
  if (config.showRecentOnly) {
    const recentThreshold = Date.now() - (config.recentHoursThreshold * 60 * 60 * 1000);
    filteredDrivers = filteredDrivers.filter(driver => 
      driver.createdAt && new Date(driver.createdAt).getTime() > recentThreshold
    );
  }
  
  // Apply attention filter
  if (config.showOnlyNeedingAttention) {
    filteredDrivers = filteredDrivers.filter(driver => 
      needsAttention(driver, config.recentHoursThreshold)
    );
  }

  // Apply sorting
  filteredDrivers.sort((a, b) => {
    let aValue: any, bValue: any;
    
    switch (config.sortBy) {
      case 'name':
        aValue = getDriverDisplayName(a);
        bValue = getDriverDisplayName(b);
        break;
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      case 'checkIn':
        aValue = a.checkIn ? new Date(a.checkIn).getTime() : 0;
        bValue = b.checkIn ? new Date(b.checkIn).getTime() : 0;
        break;
      case 'shipment':
        aValue = a.shipment?.referenceNumber || a.shipmentId;
        bValue = b.shipment?.referenceNumber || b.shipmentId;
        break;
      case 'createdAt':
        aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        break;
      default:
        aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return config.sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    } else {
      return config.sortOrder === 'asc' ? (aValue - bValue) : (bValue - aValue);
    }
  });

  // Get status counts
  const statusCounts = {
    all: drivers.length,
    needingAttention: drivers.filter(d => needsAttention(d, config.recentHoursThreshold)).length,
    pending: drivers.filter(d => d.status === 'pending').length,
    checked_in: drivers.filter(d => d.status === 'checked_in').length,
    assigned: drivers.filter(d => d.status === 'assigned').length
  };

  const displayDrivers = compactMode ? filteredDrivers.slice(0, 8) : filteredDrivers.slice(0, 12);

  return (
    <Paper sx={{ 
      p: 3, 
      height: showConfig ? 'auto' : (compactMode ? 320 : 400),
      minHeight: compactMode ? 320 : 400,
      display: 'flex',
      flexDirection: 'column',
      overflow: showConfig ? 'visible' : 'hidden'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocalShipping sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">
            {config.showOnlyNeedingAttention ? 'Drivers Needing Attention' : 'Driver Management'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Save Configuration">
            <IconButton 
              onClick={handleSaveConfig} 
              disabled={saving || !selectedFacility}
              size="small"
              color="primary"
            >
              <Save />
            </IconButton>
          </Tooltip>
          <Tooltip title="Configure">
            <IconButton 
              onClick={() => setShowConfig(!showConfig)} 
              size="small"
              color={showConfig ? 'primary' : 'default'}
            >
              <Settings />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} disabled={loading && !isRefreshing} size="small">
              <Refresh className={isRefreshing ? 'animate-spin' : ''} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Configuration Panel */}
      <Collapse in={showConfig}>
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Widget Configuration</Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={config.showOnlyNeedingAttention}
                onChange={(e) => setConfig(prev => ({ ...prev, showOnlyNeedingAttention: e.target.checked }))}
                size="small"
              />
            }
            label="Show only drivers needing attention"
            sx={{ display: 'block', mb: 1 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={config.showRecentOnly}
                onChange={(e) => setConfig(prev => ({ ...prev, showRecentOnly: e.target.checked }))}
                size="small"
              />
            }
            label="Show only recent drivers"
            sx={{ display: 'block', mb: 1 }}
          />
          
          <TextField
            label="Recent threshold (hours)"
            type="number"
            size="small"
            value={config.recentHoursThreshold}
            onChange={(e) => setConfig(prev => ({ ...prev, recentHoursThreshold: parseInt(e.target.value) || 24 }))}
            sx={{ width: 150, mb: 1 }}
            inputProps={{ min: 1, max: 168 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={config.showStatusCount}
                onChange={(e) => setConfig(prev => ({ ...prev, showStatusCount: e.target.checked }))}
                size="small"
              />
            }
            label="Show status counts"
            sx={{ display: 'block', mb: 2 }}
          />

          {/* Sort and Filter Controls */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={config.sortBy}
                label="Sort By"
                onChange={(e) => setConfig(prev => ({ ...prev, sortBy: e.target.value as any }))}
              >
                <MenuItem value="name">Driver Name</MenuItem>
                <MenuItem value="status">Status</MenuItem>
                <MenuItem value="createdAt">Created Time</MenuItem>
                <MenuItem value="checkIn">Check-in Time</MenuItem>
                <MenuItem value="shipment">Shipment</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Order</InputLabel>
              <Select
                value={config.sortOrder}
                label="Order"
                onChange={(e) => setConfig(prev => ({ ...prev, sortOrder: e.target.value as any }))}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={config.statusFilter}
                label="Status Filter"
                onChange={(e) => setConfig(prev => ({ ...prev, statusFilter: e.target.value as any }))}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="checked_in">Checked In</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Collapse>

      {!selectedFacility ? (
        <Typography variant="body2" color="warning.main">
          Select a facility to view drivers
        </Typography>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : drivers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No drivers found
        </Typography>
      ) : (
        <>
          {/* Status Overview */}
          {config.showStatusCount && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {config.showOnlyNeedingAttention && (
                <Chip 
                  label={`${statusCounts.needingAttention} Need Attention`}
                  color="error"
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip 
                label={`${statusCounts.pending} Pending`}
                color="warning"
                size="small"
                variant="outlined"
              />
              <Chip 
                label={`${statusCounts.checked_in} Checked In`}
                color="success"
                size="small"
                variant="outlined"
              />
              <Chip 
                label={`${statusCounts.assigned} Assigned`}
                color="info"
                size="small"
                variant="outlined"
              />
            </Box>
          )}

          {filteredDrivers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {config.showOnlyNeedingAttention 
                ? `No drivers need attention (${config.recentHoursThreshold}h threshold)`
                : config.showRecentOnly 
                  ? `No recent drivers (${config.recentHoursThreshold}h threshold)`
                  : "No drivers match the current filters"
              }
            </Typography>
          ) : (
            <Box sx={{ 
              flexGrow: 1, 
              overflow: 'hidden', 
              display: 'flex', 
              flexDirection: 'column',
              gap: 1
            }}>
              {/* Grid of Driver Cards */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: compactMode ? 'repeat(auto-fill, 80px)' : 'repeat(auto-fill, 100px)', 
                gap: 0.5, 
                flexGrow: 1, 
                overflow: 'auto',
                maxHeight: compactMode ? '240px' : '300px'
              }}>
                {displayDrivers.map((driver) => (
                  <Tooltip 
                    key={driver.id}
                    title={`${getDriverDisplayName(driver)} - ${getDisplayStatus(driver)} - ${driver.shipment?.referenceNumber || driver.shipmentId}`}
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        aspectRatio: '1',
                        cursor: 'pointer',
                        border: '2px solid #9CA3AF',
                        backgroundColor: getDriverCardColor(driver, config.recentHoursThreshold),
                        color: '#000000',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        transition: 'opacity 0.3s ease, transform 0.3s ease, background-color 0.3s ease',
                        '&:hover': {
                          boxShadow: 1
                        }
                      }}
                      onClick={() => handleDriverClick(driver)}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 'bold', 
                          fontSize: compactMode ? '10px' : '11px',
                          textAlign: 'center'
                        }}
                      >
                        {getDriverDisplayName(driver)}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 'bold', 
                          fontSize: compactMode ? '9px' : '10px',
                          mt: 0.3,
                          textAlign: 'center',
                          px: 0.5,
                          lineHeight: 1.2,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {getDisplayStatus(driver)}
                      </Typography>
                      {/* Show shipment reference if available */}
                      {driver.shipment?.referenceNumber && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: compactMode ? '7px' : '8px',
                            textAlign: 'center',
                            px: 0.5,
                            lineHeight: 1,
                            color: 'rgba(0,0,0,0.7)',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {driver.shipment.referenceNumber}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {filteredDrivers.length > displayDrivers.length && (
              <Typography variant="caption" color="text.secondary">
                +{filteredDrivers.length - displayDrivers.length} more {config.showOnlyNeedingAttention ? 'needing attention' : 'drivers'}
              </Typography>
            )}
            <Button 
              size="small" 
              endIcon={<ArrowForward />}
              onClick={handleViewAll}
              sx={{ ml: 'auto' }}
            >
              Manage All
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default DriverDashboardWidget; 